import React, { useCallback, useMemo } from 'react';
import loadable from '@loadable/component';
import { useDispatch, useSelector } from 'react-redux';
import LazyLoader from 'react-lazyload';
import { useLocale, useTranslate } from '@pegasus/translation';
import { useNotificationService } from '@pegasus/notification-manager';
import { getCampaignUpcomingImage } from '@browsing/campaign';
import { useConfig } from '@browsing/config';
import { getQueryParam } from '@browsing/utils';

import { RootState } from '@client/redux/types';
import { getClientDeviceHeight } from '@client/redux/modules/clientDevice';
import {
    getEAUpcomingCampaigns,
    makeGetUpcomingCampaigns,
    EA_UPCOMING_CAROUSEL_CAMPAIGNS_COUNT,
} from '@client/redux/modules/mylounge';
import { getIsSubscribedToNewsletter } from '@client/redux/modules/customer/selectors';
import { FLASH_MESSAGE_TYPE_SUCCESS } from '@client/config/constants';
import {
    showHeaderErrorFlashMessage,
    showHeaderFlashMessage,
    showHeaderSuccessFlashMessage,
} from '@client/redux/modules/flashMessage';
import { useZalandoPlus } from '@client/components/mylounge/plus';
import { usePlusExclusive } from '@browsing/hooks';
import { getCookie } from '@client/helpers/cookie';
import { InternalUpcomingSection } from './InternalUpcomingSection';

const UpcomingSection = loadable(
    () => import(/* webpackChunkName: "UpcomingSection" */ '@pegasus/upcoming-section'),
);

const UpcomingSectionWrapper: React.FC<{
    hasTitle?: boolean;
    isHorizontal?: boolean;
    customHighlightSection?: React.ReactNode;
}> = ({ hasTitle, isHorizontal = false, customHighlightSection }) => {
    const deviceHeight = useSelector(getClientDeviceHeight);
    const config = useConfig();
    const dispatch = useDispatch();
    const translate = useTranslate();
    const locale = useLocale();

    const { isEnabled: isPlusEnabled } = useZalandoPlus();
    const isPlusExclusiveEnabled = usePlusExclusive();
    const isEAUpcomingCarouselEnabled =
        config.isEnabled('cw_ea_upcoming_campaign_carousel') ||
        getCookie('zl_cw_ea_campaign_carousel') === 'true';

    const upcomingEACampaigns = useSelector(getEAUpcomingCampaigns);
    const showEAUpcomingCarousel =
        isEAUpcomingCarouselEnabled &&
        upcomingEACampaigns &&
        upcomingEACampaigns.length >= EA_UPCOMING_CAROUSEL_CAMPAIGNS_COUNT;

    const getUpcomingCampaigns = useMemo(makeGetUpcomingCampaigns, []);
    const upcomingCampaigns = useSelector((state: RootState) =>
        getUpcomingCampaigns(state, {
            isPlusEnabled,
            isPlusExclusiveEnabled,
            isEAUpcomingCarouselEnabled,
        }),
    );

    const isSubscribedToNewsletter = useSelector(getIsSubscribedToNewsletter);

    const { toggleSubscription, subscribedCampaigns } = useNotificationService();

    const onAddMessage = useCallback(() => {
        dispatch(showHeaderSuccessFlashMessage(translate('notifyMe.messageOnAdd')));
    }, [dispatch, translate]);

    const onErrorMessage = useCallback(() => {
        dispatch(showHeaderErrorFlashMessage(translate('errors.genericErrorMessage')));
    }, [dispatch, translate]);

    const handleToggleSubscription = useCallback(
        async (campaignId: string) => {
            try {
                await toggleSubscription(campaignId, onAddMessage);
            } catch (error) {
                onErrorMessage();
            }
        },
        [toggleSubscription, onAddMessage, onErrorMessage],
    );

    const reminderPopupValue = useMemo(() => {
        const eventValue = getQueryParam('event');
        if (typeof eventValue !== 'string') {
            return null;
        }

        return eventValue;
    }, []);

    const handleCountdown = useCallback(
        (message: string, callbackText: string) => {
            dispatch(
                showHeaderFlashMessage({
                    type: FLASH_MESSAGE_TYPE_SUCCESS,
                    message,
                    callbackText,
                    clickCallback: () => window.location.reload(),
                }),
            );
        },
        [dispatch],
    );

    const upcomingSectionProps = {
        hasTitle,
        isPlusEnabled,
        isPlusExclusiveEnabled,
        upcomingCampaigns: upcomingCampaigns.map(campaign => ({
            name: '',
            ...campaign,
            media: getCampaignUpcomingImage(campaign),
        })),
        showNewsletterImage: !customHighlightSection && !isSubscribedToNewsletter,
        appDomain: config.appdomainid,
        locale,
        campaignIdToHighlight: reminderPopupValue,
        onCountdownFinish: handleCountdown,
        horizontal: isHorizontal,
        customHighlightSection,
        toggleSubscription: handleToggleSubscription,
        subscribedCampaigns,
    };

    if (getQueryParam('status') === 'upcoming' || getQueryParam('status') === 'open') {
        return (
            <div id="lazyload-campaigns-upcoming">
                <InternalUpcomingSection
                    showEAUpcomingCarousel={showEAUpcomingCarousel}
                    UpcomingSectionComponent={UpcomingSection}
                    upcomingSectionProps={upcomingSectionProps}
                />
            </div>
        );
    }

    return (
        <div id="lazyload-campaigns-upcoming">
            <LazyLoader offset={deviceHeight * 2} once>
                <InternalUpcomingSection
                    showEAUpcomingCarousel={showEAUpcomingCarousel}
                    UpcomingSectionComponent={UpcomingSection}
                    upcomingSectionProps={upcomingSectionProps}
                />
            </LazyLoader>
        </div>
    );
};

export default UpcomingSectionWrapper;
